import React, { useState, useEffect, useCallback } from "react"
import { Modal, Radio, Divider, message, Spin } from "antd"
import { createResultsSD3 } from "./CreateResultsSD3"
import { download, gen, swimminglyApi } from "./utils"
import InlineTextEdit from "./InlineTextEdit"

type ExportFormat = "sd3" | "hy3" | "cl2" | "hy3cl2"
type ExportRound  = "finals" | "prelims" | "both"

interface ClubCodes {
  clubId: number
  name: string | undefined
  lsc: string
  teamCode: string
}

interface IExportResultsModalProps {
  meetId: number
  clubId: number
  isVisible: boolean
  updateVisible: (input: boolean) => void
  meetSessionContext?: {
    multiSessionMeetId: number
    sessionType: string
    sessionOrder: number
    totalSessions: number
    seriesName: string
  } | null
}

const normalizeCode = (val?: string | null) =>
  !val || val.trim() === "" ? "not set" : val

export default function ExportResultsModal({
  meetId,
  clubId,
  isVisible,
  updateVisible,
  meetSessionContext,
}: IExportResultsModalProps) {
  const [exportFormat, setExportFormat] = useState<ExportFormat>("hy3cl2")
  const [round, setRound] = useState<ExportRound>("both")
  const [clubCodesList, setClubCodesList] = useState<ClubCodes[]>([])
  const [meetInfo, setMeetInfo] = useState<{ meetName: string; meetDate: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [unknownSwimmerCount, setUnknownSwimmerCount] = useState(0)
  const [isVirtualMeet, setIsVirtualMeet] = useState(false)
  const [virtualMeetId, setVirtualMeetId] = useState<number | null>(null)
  const [exportVirtualMeetSd3, setExportVirtualMeetSd3] = useState("no")
  const [exportEntireRegularMeet, setExportEntireRegularMeet] = useState(false)

  // ── Load meet clubs + LSC/team codes ──────────────────────────────────────
  const loadClubCodes = useCallback(async () => {
    if (!meetId || !isVisible) return
    setLoading(true)
    try {
      const [meetClubsData, unknownData, virtualMeetData, meetInfoData] = await Promise.all([
        swimminglyApi.get(gen(`/api/getMeetResultClubs/${meetId}`)),
        swimminglyApi.post(gen("/api/countUnknownSwimmersInResults")).body({ meetId }),
        swimminglyApi.post(gen("/api/isItAVirtualMeet")).body({ meetId }),
        swimminglyApi.get(gen(`/api/getMeetInformation/${meetId}`)),
      ])

      if (meetInfoData?.meet?.meetName) {
        setMeetInfo({
          meetName: meetInfoData.meet.meetName,
          meetDate: meetInfoData.meet.meet_date ?? "",
        })
      }

      if (virtualMeetData?.status === "success") {
        const info = virtualMeetData.data
        if (info.isVirtualMeet === true && info.virtualMeetInfo?.length > 0) {
          setIsVirtualMeet(true)
          setVirtualMeetId(info.virtualMeetInfo[0].virtualMeetId)
        } else {
          setIsVirtualMeet(false)
          setVirtualMeetId(null)
        }
      }

      if (unknownData?.status === "success") {
        const match = (unknownData.data || []).find(
          (x: { clubId: number; unknownSwimmerCount: string }) => x.clubId === clubId,
        )
        setUnknownSwimmerCount(match ? parseInt(match.unknownSwimmerCount, 10) : 0)
      }

      if (meetClubsData?.status === "success" && Array.isArray(meetClubsData.meetClubs)) {
        const clubs: ClubCodes[] = await Promise.all(
          meetClubsData.meetClubs.map(async (c: { clubId: number; nameLong?: string }) => {
            try {
              const codeData = await swimminglyApi
                .post(gen("/api/lscAndClubCode"))
                .body({ clubId: c.clubId })
              if (codeData?.status === "success") {
                return {
                  clubId: c.clubId,
                  name: codeData.data.name_long || c.nameLong,
                  lsc: normalizeCode(codeData.data.sd3_lsc),
                  teamCode: normalizeCode(codeData.data.sd3_team_code),
                }
              }
            } catch (_) { /* fall through */ }
            return { clubId: c.clubId, name: c.nameLong, lsc: "not set", teamCode: "not set" }
          }),
        )
        setClubCodesList(clubs)
      }
    } finally {
      setLoading(false)
    }
  }, [meetId, clubId, isVisible])

  useEffect(() => {
    if (isVisible) {
      loadClubCodes()
      setExportVirtualMeetSd3("no")
      setExportEntireRegularMeet(false)
    }
  }, [isVisible, loadClubCodes])

  // ── Update one club's LSC / team code ─────────────────────────────────────
  const updateLscOrTeamCode = async (
    clubIdToUpdate: number,
    value: { lscCode: string | null; teamCode: string | null },
  ) => {
    const theData = {
      clubId: clubIdToUpdate,
      newLscCode: value.lscCode || "",
      newTeamCode: value.teamCode || "",
    }
    const resp = await fetch(gen("/api/updateLscAndClubCode"), {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      method: "post",
      body: JSON.stringify(theData),
    }).then((r) => r.json())

    if (resp.status === "success") {
      const { sd3_lsc, sd3_team_code } = resp.data
      setClubCodesList((prev) =>
        prev.map((club) =>
          club.clubId === clubIdToUpdate
            ? { ...club, lsc: normalizeCode(sd3_lsc), teamCode: normalizeCode(sd3_team_code) }
            : club,
        ),
      )
    }
  }

  // ── Download handler ──────────────────────────────────────────────────────
  const handleOk = async () => {
    setLoading(true)
    try {
      if (exportFormat === "sd3") {
        // ── Legacy SD3 path ────────────────────────────────────────────────
        const result = await fetch(gen("/api/sd3ResultsData"), {
          method: "post",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            meetId, clubId, exportEntireRegularMeet,
            virtualMeetData: exportVirtualMeetSd3, virtualMeetId,
          }),
        }).then((r) => r.json())

        const { meetResults } = result
        const teamNames: string[] = meetResults
          .map((x: any) => x.clubLongName)
          .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)

        let hasError = false
        for (const teamName of teamNames) {
          const tc = meetResults.find((x: any) => x.clubLongName === teamName)?.teamCode
          const lsc = meetResults.find((x: any) => x.clubLongName === teamName)?.lsc
          if (!tc && !lsc) {
            message.error(`SD3 Export failed: ${teamName} is missing both team code and LSC.`)
            hasError = true
          } else if (!tc) {
            message.error(`SD3 Export failed: ${teamName} is missing team code.`)
            hasError = true
          } else if (!lsc) {
            message.error(`SD3 Export failed: ${teamName} is missing LSC.`)
            hasError = true
          }
        }
        if (!hasError && meetResults.length > 0) {
          const sd3String = createResultsSD3(meetResults)
          if (sd3String) download(sd3String, `${fileBase}.sd3`, "text/plain")
        }
        return
      }

      // ── HY3 / CL2 path ────────────────────────────────────────────────────
      const params = new URLSearchParams({ format: exportFormat, round })

      if (meetSessionContext?.multiSessionMeetId) {
        const url =
          gen(`/api/meetResultsHytekExportAllSessions/${meetSessionContext.multiSessionMeetId}`) +
          `?${params.toString()}`
        await swimminglyApi.download(url, zipName(seriesBase + "-AllSessions"), document)
      } else {
        const url = gen(`/api/meetResultsHytekExport/${meetId}`) + `?${params.toString()}`
        await swimminglyApi.download(url, zipName(fileBase), document)
      }
    } catch (_err) {
      message.error("Could not download meet results")
    } finally {
      setLoading(false)
      updateVisible(false)
    }
  }

  const isMultiSession = !!meetSessionContext?.multiSessionMeetId
  const showRoundPicker = exportFormat !== "sd3"

  // ── Build a safe filename base from meet name + date ──────────────────────
  const safeBase = (raw: string) =>
    raw.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60)

  const toHytekDateStamp = (iso: string): string => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ""
    const day = String(d.getUTCDate()).padStart(2, "0")
    const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).replace(".", "")
    return `${day}${month}${d.getUTCFullYear()}`
  }

  const dateStamp = meetInfo?.meetDate ? toHytekDateStamp(meetInfo.meetDate) : ""
  const meetNameSafe = safeBase(meetInfo?.meetName ?? `Meet_${meetId}`)
  const seriesNameSafe = safeBase(meetSessionContext?.seriesName ?? meetNameSafe)
  const fileBase = `Meet Results-${meetNameSafe}${dateStamp ? `-${dateStamp}` : ""}-001`
  const seriesBase = `Meet Results-${seriesNameSafe}${dateStamp ? `-${dateStamp}` : ""}-001`

  const zipName = (base: string) => `${base}.zip`

  return (
    <Modal
      title="Export Results"
      open={isVisible}
      onCancel={() => updateVisible(false)}
      onOk={handleOk}
      okText={isMultiSession && exportFormat !== "sd3" ? "Download All Sessions" : "Download"}
      okButtonProps={{ loading }}
      width={600}
    >
      <Spin spinning={loading}>
        {unknownSwimmerCount > 0 && (
          <p style={{ color: "#fa8c16" }}>
            <b>Note:</b> {unknownSwimmerCount} swimmer
            {unknownSwimmerCount === 1 ? " does" : "s do"} not have a known USA
            Swimming ID. A temporary ID will be assigned.
          </p>
        )}

        <div style={{ marginBottom: 16 }}>
          <strong style={{ marginRight: 12 }}>Export format:</strong>
          <Radio.Group
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="sd3">SD3</Radio.Button>
            <Radio.Button value="hy3">HY3</Radio.Button>
            <Radio.Button value="cl2">CL2</Radio.Button>
            <Radio.Button value="hy3cl2">HY3 + CL2</Radio.Button>
          </Radio.Group>
        </div>

        {showRoundPicker && (
          <div style={{ marginBottom: 16 }}>
            <strong style={{ marginRight: 12 }}>Round:</strong>
            <Radio.Group
              value={round}
              onChange={(e) => setRound(e.target.value as ExportRound)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="finals">Finals only</Radio.Button>
              <Radio.Button value="prelims">Prelims only</Radio.Button>
              <Radio.Button value="both">Finals + Prelims</Radio.Button>
            </Radio.Group>
          </div>
        )}

        {exportFormat === "sd3" && isVirtualMeet && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>This is a virtual meet. What would you like to export?</strong></p>
            <Radio.Group
              value={exportVirtualMeetSd3}
              onChange={(e) => setExportVirtualMeetSd3(e.target.value)}
            >
              <Radio value="no">Export meet results (not virtual)</Radio>
              <Radio value="yes">Export virtual meet SD3</Radio>
            </Radio.Group>
          </div>
        )}

        {exportFormat === "sd3" && !isVirtualMeet && (
          <div style={{ marginBottom: 16 }}>
            <Radio.Group
              value={exportEntireRegularMeet ? "all" : "club"}
              onChange={(e) => setExportEntireRegularMeet(e.target.value === "all")}
            >
              <Radio value="club">Export my club only</Radio>
              <Radio value="all">Export entire meet</Radio>
            </Radio.Group>
          </div>
        )}

        {exportFormat !== "sd3" && clubCodesList.length > 0 && (
          <>
            <Divider titlePlacement="left" plain>
              Club LSC &amp; Team Codes
            </Divider>
            <p style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
              Each club's LSC and team code are embedded in the HY3/CL2 file.
              Click any value to edit it before downloading.
            </p>
            {clubCodesList.map((club) => (
              <div
                key={club.clubId}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 140px 140px",
                  alignItems: "center",
                  columnGap: 8,
                  marginBottom: 6,
                }}
              >
                <span style={{ fontWeight: 500 }}>{club.name || `Club ${club.clubId}`}</span>
                <span>
                  LSC:{" "}
                  <InlineTextEdit
                    updateTheValue={async (newLsc: string) =>
                      updateLscOrTeamCode(club.clubId, { lscCode: newLsc, teamCode: null })
                    }
                  >
                    {club.lsc}
                  </InlineTextEdit>
                </span>
                <span>
                  Code:{" "}
                  <InlineTextEdit
                    updateTheValue={async (newCode: string) =>
                      updateLscOrTeamCode(club.clubId, { lscCode: null, teamCode: newCode })
                    }
                  >
                    {club.teamCode}
                  </InlineTextEdit>
                </span>
              </div>
            ))}
          </>
        )}

        {isMultiSession && exportFormat !== "sd3" && (
          <>
            <Divider plain />
            <p style={{ color: "#1890ff" }}>
              This meet is part of a multi-session series. Clicking{" "}
              <b>Download All Sessions</b> will bundle one file per session into
              a single ZIP.
            </p>
          </>
        )}

        <Divider plain />
        <p style={{ fontSize: 12, color: "#8c8c8c" }}>
          HY3 and CL2 files embed each club's LSC and team code. LSC is
          typically your state abbreviation; team code is your club's 4-letter
          abbreviation. These must match the codes in your destination system.
        </p>
      </Spin>
    </Modal>
  )
}
